import { Container, Graphics, Sprite, Text } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import { Easings, TweenGroup } from '../../core/Tween';
import { woodGradient } from '../board/BoardPanel';
import { displayStyle, prefersReducedMotion } from '../board/ui';
import { glowTexture } from '../road/glow';

/** The painted tile a locked cell turns into. */
export const LOCK_TILE_ALIAS = 'shared:reel:hns_lock_tile';

export interface MoneyValueChipOptions {
  /** Coin value as a multiple of the bet: picks the coin art (a coin, a
   *  stack, a bag or a chest), so bigger prizes look bigger. */
  betMultiplier?: number;
}

/**
 * A locked cell in the hold-and-spin bonus (producer 2026-10-14: painted
 * pieces, never a flat circle with a number on it): the cell turns into a
 * glowing gilded tile holding the coin's art, with what it's worth on a
 * small wooden plate along the bottom. Pops in when it lands and holds
 * still across the respins.
 */
export class MoneyValueChip extends Container {
  private readonly glow = new Sprite(glowTexture());
  private readonly tile = new Sprite();
  private readonly coin = new Sprite();
  private readonly plate = new Graphics();
  private readonly valueText: Text;
  private readonly tweens = new TweenGroup();

  constructor(value: number, size: number, options: MoneyValueChipOptions = {}) {
    super();
    this.alpha = 0;
    this.scale.set(0.5);
    for (const s of [this.glow, this.tile, this.coin]) s.anchor.set(0.5);
    this.glow.blendMode = 'add';
    this.glow.tint = 0xffc457;
    this.valueText = new Text({ text: formatValue(value), style: displayStyle(Math.max(13, Math.round(size * 0.19))) });
    this.valueText.anchor.set(0.5);
    this.addChild(this.glow, this.tile, this.coin, this.plate, this.valueText);
    this.draw(size, options.betMultiplier);
  }

  /** Pop-in animation: call once, right after adding to the stage. */
  popIn(delayMs: number = 0): void {
    if (delayMs > 0) {
      setTimeout(() => this.runPopIn(), delayMs);
    } else {
      this.runPopIn();
    }
  }

  private runPopIn(): void {
    // popIn's delay can outlive the chip (the round ends and destroyChip
    // runs first); starting a tween on a destroyed chip throws on scale.
    if (this.destroyed) return;
    if (prefersReducedMotion()) {
      this.alpha = 1;
      this.scale.set(1);
      return;
    }
    this.tweens.start({
      durationMs: 360,
      easing: Easings.easeOutBack,
      onUpdate: (p) => {
        if (this.destroyed) return;
        this.alpha = Math.min(1, p * 1.6);
        this.scale.set(0.5 + p * 0.5);
        this.glow.alpha = 0.55 + 0.45 * Math.sin(p * Math.PI);
      },
    });
  }

  /** Brief pulse, when the round ends, as each coin is counted. */
  pulse(): void {
    // The chip can be destroyed with its parent mid-pulse (end of the
    // hold-and-spin round), which skips destroyChip's tween cancel.
    this.tweens.start({
      durationMs: 480,
      easing: Easings.linear,
      onUpdate: (p) => {
        if (this.destroyed) return;
        const phase = p < 0.5 ? p * 2 : 2 - p * 2;
        this.scale.set(1 + Easings.easeOutCubic(phase) * 0.14);
        this.glow.alpha = 0.55 + 0.45 * phase;
      },
      onComplete: () => {
        if (!this.destroyed) this.scale.set(1);
      },
    });
  }

  private draw(size: number, betMultiplier: number | undefined): void {
    const loader = AssetLoader.shared;
    this.glow.width = this.glow.height = size * 1.25;
    this.glow.alpha = 0.55;

    const tile = loader.getTexture(LOCK_TILE_ALIAS);
    if (tile) this.tile.texture = tile;
    this.tile.visible = tile !== null;
    this.tile.width = this.tile.height = size * 0.96;

    // The coin's art by size of prize, or the plain coin if that hasn't
    // loaded: never a drawn circle.
    const coin = loader.getTexture(coinTierAlias(betMultiplier)) ?? loader.getTexture('shared:icon:coin');
    if (coin) this.coin.texture = coin;
    this.coin.visible = coin !== null;
    const art = size * 0.7;
    const k = coin ? art / Math.max(coin.width, coin.height) : 1;
    this.coin.scale.set(k);
    this.coin.y = -size * 0.08;

    // The value on a small wooden plate across the bottom of the tile.
    const w = Math.min(size * 0.86, this.valueText.width + size * 0.2);
    const h = this.valueText.height * 0.92;
    const y = size * 0.3;
    this.plate
      .clear()
      .roundRect(-w / 2, y - h / 2 + 2, w, h, h * 0.35)
      .fill({ color: 0x000000, alpha: 0.35 })
      .roundRect(-w / 2, y - h / 2, w, h, h * 0.35)
      .fill(woodGradient())
      .stroke({ color: 0xd9a441, width: 1.5 });
    this.valueText.y = y + 1;
    if (this.valueText.width > size * 0.8) this.valueText.scale.set((size * 0.8) / this.valueText.width);
  }

  destroyChip(): void {
    this.tweens.cancelAll();
    super.destroy({ children: true });
  }
}

/** Map a bet-multiplier to its coin-art tier alias. Thresholds bracket
 *  the value pool (1,2: a coin; 5,10: a stack; 25: a bag; 100,250: a
 *  chest). No multiplier: the single coin. */
export function coinTierAlias(mult: number | undefined): string {
  if (mult === undefined || !Number.isFinite(mult) || mult <= 3) return 'shared:reel:coin_single';
  if (mult <= 12) return 'shared:reel:coin_stack';
  if (mult <= 50) return 'shared:reel:coin_bag';
  return 'shared:reel:coin_chest';
}

function formatValue(value: number): string {
  if (value >= 1000) {
    // 1.2k, 12k, 120k: one decimal under 10k.
    return value < 10_000
      ? `${(value / 1000).toFixed(1).replace(/\.0$/, '')}k`
      : `${Math.round(value / 1000)}k`;
  }
  return value.toLocaleString('en-GB');
}
